import { useEffect, useRef, useState } from "react";
import type { FC } from "react";
import { useTheme } from "./hooks";

/**
 * The pixel grades of the heading face, coarsest first, as cells per em.
 * Past the last grade the heading is set in the face itself.
 */
export const GRADES = [5, 7, 10, 14, 20, 30];

interface Props {
  lines: string[];
  /** Index into GRADES; GRADES.length (or more) means the plain face. */
  grade: number;
  /** The element it is set as; a page has one heading, so anything else is a paragraph. */
  tag?: "h1" | "p";
  className?: string;
}

/**
 * A heading in one pixel grade of its own typeface: the text is drawn on a
 * grid of square cells and each cell is either on or off. The real text stays
 * in the document underneath, so it is still read and selected as a heading.
 */
export const PixelType: FC<Props> = ({ lines, grade, tag: Tag = "h1", className }) => {
  const text = useRef<HTMLElement | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [tick, setTick] = useState(0);
  const [theme] = useTheme();
  const plain = grade >= GRADES.length;

  // Redraw once the heading face has loaded, and whenever the heading changes size.
  useEffect(() => {
    const el = text.current;
    if (!el) return;
    const bump = () => setTick(n => n + 1);
    document.fonts.ready.then(bump);
    const watch = new ResizeObserver(bump);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  useEffect(() => {
    const el = text.current;
    const cv = canvas.current;
    const ctx = cv?.getContext("2d", { willReadFrequently: true });
    if (!el || !cv || !ctx || plain) return;

    const style = getComputedStyle(el);
    const size = parseFloat(style.fontSize);
    const cell = Math.max(2, Math.round(size / GRADES[grade]));
    const box = el.getBoundingClientRect();
    const cols = Math.ceil(box.width / cell) + 1;
    const rows = Math.ceil(box.height / cell) + 1;
    cv.width = cols;
    cv.height = rows;
    cv.style.width = `${cols * cell}px`;
    cv.style.height = `${rows * cell}px`;

    // Draw the lines small, one canvas pixel per cell, where the document has them.
    ctx.setTransform(1 / cell, 0, 0, 1 / cell, 0, 0);
    ctx.font = `${style.fontWeight} ${size}px ${style.fontFamily}`;
    ctx.fillStyle = "#fff";
    const spacing = parseFloat(style.letterSpacing) || 0;
    const upper = style.textTransform === "uppercase";
    for (const line of el.querySelectorAll("span")) {
      const words = upper ? (line.textContent ?? "").toUpperCase() : (line.textContent ?? "");
      const at = line.getBoundingClientRect();
      const m = ctx.measureText(words);
      const y =
        at.top -
        box.top +
        (at.height - m.fontBoundingBoxAscent - m.fontBoundingBoxDescent) / 2 +
        m.fontBoundingBoxAscent;
      let x = at.left - box.left;
      for (const letter of words) {
        ctx.fillText(letter, x, y);
        x += ctx.measureText(letter).width + spacing;
      }
    }

    // Each cell is on or off: no greys, so the edges stay square.
    const ink = (style.color.match(/[\d.]+/g) ?? ["242", "242", "238"]).map(Number);
    const image = ctx.getImageData(0, 0, cols, rows);
    const px = image.data;
    for (let i = 0; i < px.length; i += 4) {
      const on = px[i + 3] > 105;
      px[i] = ink[0];
      px[i + 1] = ink[1];
      px[i + 2] = ink[2];
      px[i + 3] = on ? 255 : 0;
    }
    ctx.putImageData(image, 0, 0);
  }, [grade, plain, tick, lines, theme]);

  return (
    <Tag
      ref={(el: HTMLElement | null) => {
        text.current = el;
      }}
      className={className ? `sd-title ${className}` : "sd-title"}
      data-pixel={plain ? undefined : ""}
    >
      {lines.map(line => (
        <span key={line}>{line}</span>
      ))}
      <canvas ref={canvas} aria-hidden />
    </Tag>
  );
};
